import { inkAppearance } from './inkAppearance';
import styles from "./Square.module.css";
import { PieceType, State, Mode, Position } from "../services/zodiacEngine/types";
import { IPiece } from "../services/zodiacEngine/types";
import React, { useContext } from "react";
import { GameContext } from "./Providers/GameContext";
import { shouldTutorialPieceShowCrown } from "./Tutorials/TutorialSpecs";

type SquareRendererProps = {
  pieces: IPiece[];
  handleSelect: (piece: IPiece | undefined) => void;
  position?: Position;
  onPieceDragStart?: (event: React.DragEvent<HTMLImageElement>, piece: IPiece, position: Position) => void;
  designateMode?: boolean;
};

export default function SquareRenderer(props: SquareRendererProps) {
  const { handleSelect: selectActualPiece, position, onPieceDragStart, designateMode } = props;
  const isDraggableMode = !!onPieceDragStart && !!position;
  const { gameState, isLightPlayer, getKingGuessForPiece, isSpectating, selectedPiece, kingHighlightActive } = useContext(GameContext)!

  const pieces = inkAppearance(props.pieces, gameState.board);
  const actualPiece = (piece: IPiece | undefined) => props.pieces.find(p => p.id === piece?.id) || props.pieces.find(p => p.type === PieceType.Octopus);
  const handleSelect = (piece: IPiece | undefined) => selectActualPiece(actualPiece(piece));

  // SECURITY FIX: Detect if shadows are active and what color they are
  // When shadows exist, ALL octopi of that color must look identical (including the real one)
  // This prevents the opponent from identifying the real octopus via visual differences
  const getShadowColor = (): 'light' | 'dark' | null => {
    if (!gameState?.board) return null;
    for (const row of gameState.board) {
      for (const square of row) {
        if (!square?.Pieces) continue;
        for (const piece of square.Pieces) {
          if (piece.isShadow && piece.type === PieceType.Octopus) {
            return piece.isLight ? 'light' : 'dark';
          }
        }
      }
    }
    return null;
  };

  // Cache the shadow color to avoid recalculating for each piece
  const activeShadowColor = getShadowColor();

  // Helper function to check if a piece is the player's king and should show a crown
  const shouldShowCrown = (piece: IPiece): boolean => {
    // Only show crown during actual gameplay
    if (gameState.state !== State.Playing) {
      return false;
    }

    if (piece.isShadow || piece.isShadowed) return false;

    // Replay mode (Mode.Dev) or Sandbox mode (puzzles): show crowns for any piece marked isKing (both sides)
    if (gameState.mode === Mode.Dev || gameState.mode === Mode.Sandbox) {
      return piece.isKing === true;
    }

    // CRITICAL: Never show crown to spectators - they shouldn't know which piece is the king
    if (isSpectating) {
      return false;
    }

    // "Show King" button override - temporarily show crown for player's own king
    // Uses gameState.lightKing/darkKing (PieceType) since piece.isKing may not be set in multiplayer
    const playerIsLight = isLightPlayer();
    const playerKingType = playerIsLight ? gameState.lightKing : gameState.darkKing;
    if (!piece.isShadow && !piece.isShadowed && kingHighlightActive && piece.type === playerKingType && piece.isLight === playerIsLight) {
      return true;
    }

    // CRITICAL: Never show crown on shadowed/inked pieces!
    // This prevents revealing the king's identity through octopus ink
    // Also hide crown when shadows are active for octopi of that color (robust check)
    const isOctopusWithActiveShadows = piece.type === PieceType.Octopus &&
      activeShadowColor !== null &&
      (piece.isLight ? 'light' : 'dark') === activeShadowColor;

    if (piece.isShadow || piece.isShadowed || isOctopusWithActiveShadows) {
      return false;
    }

    // Check for tutorial-specific crown logic
    const tutorialConfig = (gameState as any).tutorialConfig;
    if (tutorialConfig) {
      // Use realConstellation if available (for FEN overrides), otherwise use constellation
      const actualConstellation = tutorialConfig.realConstellation || tutorialConfig.constellation;
      // Use realTutorialIndex if available (for correct tutorial identity), otherwise use tutorialIndex
      const actualTutorialIndex = tutorialConfig.realTutorialIndex !== undefined ? tutorialConfig.realTutorialIndex : tutorialConfig.tutorialIndex;

      if (shouldTutorialPieceShowCrown(
        actualConstellation,
        actualTutorialIndex,
        piece.type,
        piece.isKing,
        piece.isLight
      )) {
        return true;
      }
    }

    // Challenge AI specific logic
    const challengeConfig = (gameState as any).challengeAIConfig;
    if (challengeConfig) {
      const { lightPlayer, darkPlayer, showKings } = challengeConfig;
      const isAIvsAI = lightPlayer !== 'player' && darkPlayer !== 'player';
      const isHumanVsAI = (lightPlayer === 'player' && darkPlayer !== 'player') || (lightPlayer !== 'player' && darkPlayer === 'player');

      // AI vs AI: Show BOTH crowns if showKings toggle is ON (regardless of whose turn it is)
      if (isAIvsAI) {
        return piece.isKing && showKings;
      }

      // Human vs AI: Show ONLY the human player's king (so they know which piece is their king)
      if (isHumanVsAI) {
        const isHumanLight = lightPlayer === 'player';
        const isHumanPiece = piece.isLight === isHumanLight;
        // Show crown only for the human player's king, not the AI's king (fog of war for AI king)
        return piece.isKing && isHumanPiece;
      }

      // Human vs Human (Local 2P): HIDE crowns to maintain fog of war
      // Neither player should see which piece is the opponent's king
      if (lightPlayer === 'player' && darkPlayer === 'player') {
        return false;
      }

      // If we have challengeConfig but none of the above conditions matched,
      // don't fall through to default behavior
      return false;
    }

    // Default behavior: Check if this piece is a king and belongs to the current player
    return piece.isKing && piece.isLight === isLightPlayer();
  };

  // Helper function to determine CSS classes for a piece
  const getPieceClasses = (piece: IPiece, baseClass: string): string[] => {
    const pieceClasses = [baseClass];

    const isSelected = !piece.isShadowed && !piece.isShadow && selectedPiece && selectedPiece.piece.id === piece.id;

    // Determine status effect - use combined selected+effect class when selected,
    // otherwise use the standalone effect class. This prevents CSS filter conflicts
    // where selectedPieceGlow's drop-shadow would override the effect's color filter.
    const hasStatusEffect =
      piece.isMeadowed ||
      piece.poisonedStatus?.isPoisoned ||
      piece.zombifiedStatus?.isZombified ||
      piece.isEntangled ||
      piece.isInfected;

    if (isSelected && !hasStatusEffect) {
      pieceClasses.push(styles.selectedPieceGlow);
    }

    if (piece.isMeadowed) {
      pieceClasses.push(isSelected ? styles.selectedMeadowed : styles.meadowedPiece);
    }
    if (piece.poisonedStatus?.isPoisoned) {
      pieceClasses.push(isSelected ? styles.selectedPoisoned : styles.poisonedPiece);
    }
    if (piece.zombifiedStatus?.isZombified) {
      pieceClasses.push(isSelected ? styles.selectedZombified : styles.zombifiedPiece);
    }
    if (piece.isEntangled) {
      pieceClasses.push(isSelected ? styles.selectedEntangled : styles.entangledPiece);
    }
    if (piece.isInfected) {
      pieceClasses.push(isSelected ? styles.selectedInfected : styles.infectedPiece);
    }
    if (piece.isFainted) {
      pieceClasses.push(
        piece.isLight ? styles.lightFaintedPiece : styles.darkFaintedPiece
      );
    }
    if (piece.type === PieceType.Virus) {
      pieceClasses.push(styles.virusPiece);
    }
    // SECURITY FIX: When shadows are active, ALL octopi of that color must look identical
    // This ensures the real octopus is indistinguishable from shadows
    // Uses shadowedPiece class for: shadows, the shadowed octopus, AND any octopus of the shadow color
    const isOctopusWithActiveShadows = piece.type === PieceType.Octopus &&
      activeShadowColor !== null &&
      (piece.isLight ? 'light' : 'dark') === activeShadowColor;

    if (piece.isShadow || piece.isShadowed || isOctopusWithActiveShadows) {
      pieceClasses.push(styles.shadowedPiece);
    }
    // Show King highlight - gold glow when player presses "Show King" button
    {
      const playerIsLight = isLightPlayer();
      const playerKingType = playerIsLight ? gameState.lightKing : gameState.darkKing;
      if (kingHighlightActive && piece.type === playerKingType && piece.isLight === playerIsLight) {
        pieceClasses.push(styles.kingHighlightGlow);
      }
    }
    return pieceClasses;
  };

  // Helper function to get the piece image path
  const getPieceImagePath = (piece: IPiece): string => {
    // Default image path - use absolute path with correct case
    let pieceImagePath = `/Icons/${piece.type}-${
      piece.isLight ? "light" : "dark"
    }.png`;

    // Fringe case: If the piece is a goat and has fainted, use the HG images
    if (piece.type === PieceType.Goat && piece.isFainted) {
      pieceImagePath = `/Icons/HG-${piece.isLight ? "light" : "dark"}.png`;
    }
    // Fringe case: If the piece is a Frog and hasn't moved, use the lily images
    else if (piece.type === PieceType.Frog && !piece.hasLilyJumped) {
      pieceImagePath = `/Icons/${
        piece.isLight ? "lightlily2" : "darklily2"
      }.png`;
    }
    // Fringe case: If the piece is a snake that has used Shed, it gets a colorful new skin
    else if (piece.type === PieceType.Snake && piece.hasShed) {
      pieceImagePath = `/Icons/${
        piece.isLight ? "Snake-light-shead" : "Snake-dark-shead"
      }.png`;
    }

    return pieceImagePath;
  };

  // Utility: Map direction string to angle in degrees (0 = up, 90 = right, etc.)
  function getPenguinRotation(piece: IPiece): number {
    if (piece.type !== PieceType.Penguin || !piece.direction) {
      // Debug: Log when penguin has no direction
      if (piece.type === PieceType.Penguin && !piece.direction) {
      }
      return 0;
    }

    // FIXED: Base penguin image faces DOWN by default
    // Dark penguins get 'up' direction, Light penguins get 'down' direction
    // So we rotate from the base down-facing orientation
    const baseRotation = (() => {
      switch (piece.direction) {
        case 'up': return 180;      // Face up (rotate 180° from down base)
        case 'up-right': return 225; // Face up-right
        case 'right': return 270;   // Face right
        case 'down-right': return 315; // Face down-right
        case 'down': return 0;      // Face down (base orientation, no rotation)
        case 'down-left': return 45; // Face down-left
        case 'left': return 90;     // Face left
        case 'up-left': return 135; // Face up-left
        default: return 0; // Default to down (base orientation)
      }
    })();

    // When board is flipped for dark player, apply vertical flip to rotation
    // Light player sees flipped board (row 7 at top), dark player sees normal board (row 0 at top)
    // So we need to flip the visual rotation for dark player
    const shouldFlipRotation = !isLightPlayer();
    const rotation = shouldFlipRotation
      ? (540 - baseRotation) % 360  // Vertical flip formula
      : baseRotation;

    return rotation;
  }

  // Helper function to render the piece image
  const renderPieceImage = (
    piece: IPiece,
    pieceClasses: string[],
    extraProps: React.ImgHTMLAttributes<HTMLImageElement> = {}
  ): JSX.Element => {
    const pieceImagePath = getPieceImagePath(piece);
    // Add penguin rotation via CSS custom property so it survives CSS animations
    let style = extraProps.style || {};
    if (piece.type === PieceType.Penguin && piece.direction) {
      style = {
        ...style,
        '--penguin-rotation': `${getPenguinRotation(piece)}deg`
      } as React.CSSProperties;
      pieceClasses = [...pieceClasses, styles.penguinPiece];
    }

    const pieceElement = (
      <img
        className={pieceClasses.join(" ")}
        src={pieceImagePath}
        alt={piece.type}
        draggable={isDraggableMode ? "true" : "false"}
        onDragStart={isDraggableMode ? (e) => onPieceDragStart!(e, actualPiece(piece)!, position!) : undefined}
        onClick={(e) => {
          if (designateMode) return;
          handleSelect(piece);
        }}
        onError={(e) => {
          // Retry loading once if the image fails (handles cached 404s from CDN)
          const img = e.currentTarget;
          if (!img.dataset.retried) {
            img.dataset.retried = 'true';
            img.src = pieceImagePath + '?retry=1';
          }
        }}
        style={designateMode ? { ...style, cursor: 'crosshair' } : isDraggableMode ? { ...style, cursor: 'grab' } : style}
        {...extraProps}
      />
    );

    // If this piece should show a crown, wrap it in a crown container
    if (shouldShowCrown(piece)) {
      const plrIsLight = isLightPlayer();
      const plrKingType = plrIsLight ? gameState.lightKing : gameState.darkKing;
      const isHighlighted = kingHighlightActive && piece.type === plrKingType && piece.isLight === plrIsLight;
      return (
        <div className={`${styles.kingContainer} ${isHighlighted ? styles.kingHighlightGlow : ''}`}>
          {pieceElement}
          <img
            className={styles.crownOverlay}
            src="/Icons/crown1.png"
            alt="King Crown"
            draggable="false"
            onClick={(e) => {
              if (designateMode) return;
              handleSelect(piece);
            }}
            onError={(e) => {
              const img = e.currentTarget;
              if (!img.dataset.retried) {
                img.dataset.retried = 'true';
                img.src = '/Icons/crown1.png?retry=1';
              }
            }}
          />
        </div>
      );
    }

    // Check if this piece has a king guess on it (taunt feature)
    const guessOnPiece = !piece.isShadowed && !piece.isShadow && getKingGuessForPiece(piece.id);
    if (guessOnPiece) {
      const guessIcon = guessOnPiece.guesserIsLight
        ? '/Icons/crownguesslight.png'
        : '/Icons/crownguessdark.png';

      return (
        <div className={styles.kingGuessContainer}>
          {pieceElement}
          <img
            className={styles.crownGuessOverlay}
            src={guessIcon}
            alt="King Guess"
            draggable="false"
            onClick={(e) => {
              if (designateMode) return;
              handleSelect(piece);
            }}
            onError={(e) => {
              const img = e.currentTarget;
              if (!img.dataset.retried) {
                img.dataset.retried = 'true';
                img.src = guessIcon + '?retry=1';
              }
            }}
          />
        </div>
      );
    }

    return pieceElement;
  };

  const getDefaultElement = (): JSX.Element => {
    // Find the tree and virus pieces on the square
    const treePiece = pieces.find((piece) => piece.type === PieceType.Tree);
    const virusPiece = pieces.find((piece) => piece.type === PieceType.Virus);

    // If there are multiple pieces including a tree or virus, show floating icons
    if (pieces.length > 1) {
      // === 3-PIECE STACKS (check before 2-piece paths) ===

      // Scenario 1: Tree + Animal + Virus (infected animal housed in tree)
      // Render tree and animal side-by-side (like normal housing) with virus badge on the animal
      if (pieces.length === 3 && treePiece && virusPiece) {
        const animalPiece = pieces.find(
          (piece) => piece.type !== PieceType.Tree && piece.type !== PieceType.Virus
        );
        if (animalPiece) {
          return (
            <div className={styles.floatingIcons}>
              {renderPieceImage(
                treePiece,
                getPieceClasses(treePiece, styles.floatingIcon)
              )}
              {/* Animal with virus badge in top-right */}
              <div className={styles.floatingIconWrapper}>
                {renderPieceImage(
                  animalPiece,
                  getPieceClasses(animalPiece, styles.floatingIcon)
                )}
                {renderPieceImage(
                  virusPiece,
                  getPieceClasses(virusPiece, styles.virusBadge)
                )}
              </div>
            </div>
          );
        }
      }

      // Scenario 2: Fainted Goat + Piece + Virus (infected piece on fainted goat)
      if (pieces.length === 3 && virusPiece) {
        const faintedGoatTriple = pieces.find(
          (piece) => piece.type === PieceType.Goat && piece.isFainted
        );
        if (faintedGoatTriple) {
          const standingPiece = pieces.find(
            (piece) => piece !== faintedGoatTriple && piece.type !== PieceType.Virus
          );
          if (standingPiece) {
            return (
              <div className={styles.stoodOnContainer}>
                {renderPieceImage(
                  faintedGoatTriple,
                  getPieceClasses(faintedGoatTriple, styles.stoodOnGoat)
                )}
                {/* Wrap standing piece + virus badge together */}
                <div className={styles.standingPiece}>
                  {renderPieceImage(
                    standingPiece,
                    getPieceClasses(standingPiece, styles.squareicon)
                  )}
                  {renderPieceImage(
                    virusPiece,
                    getPieceClasses(virusPiece, styles.virusBadge)
                  )}
                </div>
              </div>
            );
          }
        }
      }

      // === END 3-PIECE STACKS ===

      // Check if one of the pieces is a fainted goat
      const faintedGoat = pieces.find(
        (piece) => piece.type === PieceType.Goat && piece.isFainted
      );
      const otherPiece = pieces.find(
        (piece) => !(piece.type === PieceType.Goat && piece.isFainted)
      );

      // Special case: If we have exactly two pieces and one is a fainted goat
      if (pieces.length === 2 && faintedGoat && otherPiece) {
        // Create a stacked appearance where the other piece "stands on" the fainted goat
        return (
          <div className={styles.stoodOnContainer}>
            {/* Render the fainted goat underneath */}
            {renderPieceImage(
              faintedGoat,
              getPieceClasses(faintedGoat, styles.stoodOnGoat)
            )}
            {/* Render the other piece on top */}
            {renderPieceImage(
              otherPiece,
              getPieceClasses(otherPiece, styles.standingPiece)
            )}
          </div>
        );
      }

      // Virus + Animal (2-piece): show animal at full size with virus badge in top-right
      if (pieces.length === 2 && virusPiece) {
        const infectedAnimal = pieces.find(
          (piece) => piece.type !== PieceType.Virus
        );
        if (infectedAnimal) {
          return (
            <div className={styles.infectedPieceContainer}>
              {renderPieceImage(
                infectedAnimal,
                getPieceClasses(infectedAnimal, styles.squareicon)
              )}
              {renderPieceImage(
                virusPiece,
                getPieceClasses(virusPiece, styles.virusBadge)
              )}
            </div>
          );
        }
      }

      // Find other pieces that are not the tree or virus
      const otherPieces = pieces.filter(
        (piece: IPiece) =>
          piece.type !== PieceType.Tree && piece.type !== PieceType.Virus
      );

      // Prepare an array to hold the floating pieces
      const floatingPieces: IPiece[] = [];

      // Add the tree or virus piece to the floating pieces
      if (treePiece) {
        floatingPieces.push(treePiece);
      } else if (virusPiece) {
        floatingPieces.push(virusPiece);
      }

      // Add the other pieces to the floating pieces
      floatingPieces.push(...otherPieces);

      // Show floating icons for both tree/virus and the other piece
      return (
        <div className={styles.floatingIcons}>
          {floatingPieces.map((piece: IPiece, index: number) => {
            const pieceClasses = getPieceClasses(piece, styles.floatingIcon);
            const pieceImagePath = getPieceImagePath(piece);

            // Add penguin rotation if needed
            let style: React.CSSProperties = {};
            let extraClasses = [...pieceClasses];
            if (piece.type === PieceType.Penguin && piece.direction) {
              style = {
                '--penguin-rotation': `${getPenguinRotation(piece)}deg`,
              } as React.CSSProperties;
              extraClasses.push(styles.floatingPenguin);
            }

            return (
              <img
                key={index}
                className={extraClasses.join(" ")}
                src={pieceImagePath}
                alt={piece.type}
                draggable={isDraggableMode ? "true" : "false"}
                onDragStart={isDraggableMode ? (e) => onPieceDragStart!(e, actualPiece(piece)!, position!) : undefined}
                style={designateMode ? { ...style, cursor: 'crosshair' } : isDraggableMode ? { ...style, cursor: 'grab' } : style}
                onClick={() => {
                  if (designateMode) return;
                  handleSelect(piece);
                }}
                onError={(e) => {
                  const img = e.currentTarget;
                  if (!img.dataset.retried) {
                    img.dataset.retried = 'true';
                    img.src = pieceImagePath + '?retry=1';
                  }
                }}
              />
            );
          })}
        </div>
      );
    }
    // Else if there is a tree or virus piece
    else if (treePiece || virusPiece) {
      const piece = treePiece || virusPiece;
      if (!piece) {
        return <></>; // or handle the undefined case appropriately
      }
      const pieceClasses = getPieceClasses(piece, styles.squareicon);

      return renderPieceImage(piece, pieceClasses);
    }
    // Else, if there's a single piece that's not a tree or virus
    else if (pieces.length === 1) {
      const piece = pieces[0];
      const pieceClasses = getPieceClasses(piece, styles.squareicon);

      return renderPieceImage(piece, pieceClasses);
    } else {
      return (
        <div
          className={styles.emptySquare}
          style={designateMode ? { cursor: 'crosshair' } : undefined}
          onClick={() => { if (!designateMode) handleSelect(undefined); }}
        ></div>
      ); // Return empty if no pieces are present
    }
  };

  return <>{getDefaultElement()}</>;
}
